/**
 * Contacts API Integration - Type Definitions
 * 
 * Standalone type definitions for Webex Contacts API integration.
 * Completely decoupled from DSS implementation.
 */

/**
 * Contact object from Webex SDK
 */
export interface WebexContact {
  contactId: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  phoneNumbers?: ContactPhoneNumber[];
  emails?: ContactEmail[];
  contactType: 'CUSTOM' | 'CLOUD';
  groupId?: string[];
  avatarURL?: string;
  encryptedDisplayName?: string;
  encryptedFields?: string[];
}

/**
 * Phone number within a contact
 */
export interface ContactPhoneNumber {
  type: string;      // 'work', 'mobile', 'home', etc.
  value: string;     // E.164 or formatted phone number
  primary?: boolean;
}

/**
 * Email within a contact
 */
export interface ContactEmail {
  type: string;
  value: string;
  primary?: boolean;
}

/**
 * Simplified contact entry stored in cache
 * Optimized for fast lookups and minimal storage
 */
export interface ContactEntry {
  contactId: string;
  displayName: string;
  firstName?: string;
  lastName?: string;
  contactType: 'CUSTOM' | 'CLOUD';
  phones: string[];                // All normalized phone numbers
  primaryPhone?: string;           // Primary phone (if set)
  avatarURL?: string;
}

/**
 * Cache structure stored in localStorage
 * Includes metadata for TTL and versioning
 */
export interface WebexContactsCache {
  version: number;                 // Schema version (current: 1)
  userId: string;                  // Scoped to user
  fetchedAt: number;               // Unix timestamp (ms)
  expiresAt: number;               // fetchedAt + TTL
  totalContacts: number;           // Count for metrics
  phoneMap: Record<string, ContactEntry>;  // Phone→Contact map
}

/**
 * Response from Webex Contacts API
 */
export interface ContactsAPIResponse {
  statusCode: number;
  message?: string;
  data: {
    contacts: WebexContact[];
  };
}

/**
 * Statistics about the contacts cache
 */
export interface ContactsCacheStats {
  totalContacts: number;
  totalPhones: number;
  fetchedAt: number;
  expiresAt: number;
  isExpired: boolean;
  cacheSize: number;  // bytes
  userId: string;
}

/**
 * Performance metrics for monitoring
 */
export interface ContactsPerformanceMetrics {
  fetchDuration: number;           // API call time (ms)
  buildMapDuration: number;        // Processing time (ms)
  cacheSaveTime: number;           // Storage write time (ms)
  cacheLoadTime: number;           // Storage read time (ms)
  lookupAvgTime: number;           // Average lookup time (ms)
  memoryUsage: number;             // Estimated bytes
  totalContacts: number;
  totalPhones: number;
}

/**
 * Configuration options for ContactsManager
 */
export interface ContactsManagerConfig {
  userId: string;
  cacheTTL?: number;               // Cache time-to-live (ms), default: 12h
  autoRefresh?: boolean;           // Auto-refresh when expired, default: true
  enableTelemetry?: boolean;       // Enable performance metrics, default: true
}

/**
 * Telemetry events for monitoring
 */
export type ContactsTelemetryEvent =
  | { type: 'init_started'; userId: string }
  | { type: 'init_success'; duration: number; totalContacts: number; cacheHit: boolean }
  | { type: 'init_failed'; error: string; duration: number }
  | { type: 'fetch_started'; userId: string }
  | { type: 'fetch_success'; duration: number; totalContacts: number; totalPhones: number }
  | { type: 'fetch_failed'; error: string; statusCode?: number }
  | { type: 'lookup_hit'; phone: string; displayName: string }
  | { type: 'lookup_miss'; phone: string }
  | { type: 'cache_load_success'; duration: number; totalContacts: number; age: number }
  | { type: 'cache_save_success'; duration: number; size: number }
  | { type: 'cache_expired'; age: number }
  | { type: 'storage_quota_exceeded'; cacheSize: number }
  | { type: 'decryption_error'; contactId: string; error: string };

/**
 * Telemetry handler interface
 */
export interface ContactsTelemetryHandler {
  emit(event: ContactsTelemetryEvent): void;
}

/**
 * Contacts lookup result for enrichment
 */
export interface ContactsLookupResult {
  contactId: string;
  displayName: string;
  firstName?: string;
  lastName?: string;
  contactType: 'CUSTOM' | 'CLOUD';
  phones: string[];
  avatarURL?: string;
  source: 'webex-contacts';
}

/**
 * Batch lookup result
 */
export interface ContactsBatchLookupResult {
  found: Map<string, ContactsLookupResult>;
  notFound: string[];
}

/**
 * Feature flags for contacts integration
 */
export interface ContactsFeatureFlags {
  enableContactsEnrichment: boolean;
  contactsAutoRefresh: boolean;
  contactsCacheTTL: number;
}

/** Fired when the contacts phone map is built (cache hit or API fetch). */
export const CONTACTS_DATA_READY_EVENT = 'contacts-data-ready';

export function dispatchContactsDataReady(userId?: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(CONTACTS_DATA_READY_EVENT, {
      detail: { userId },
    })
  );
}
